<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-h5 primary--text d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-icon left color="primary">mdi-account-plus</v-icon>
              Cadastro de Lead
            </div>
            <v-btn
              color="secondary"
              outlined
              small
              @click="showLeadsList = !showLeadsList"
              :disabled="loading"
            >
              <v-icon left small>{{ showLeadsList ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              {{ showLeadsList ? 'Ocultar' : 'Exibir' }} Listagem
            </v-btn>
          </v-card-title>
          
          <v-divider></v-divider>
          
          <v-card-text>

              <!-- Listagem de Leads -->
              <template v-if="showLeadsList">
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between align-center mb-4">
                  <h3 class="text-h6 primary--text mb-0">
                    <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
                    Leads Cadastrados
                  </h3>
                </div>

                <!-- Campo de pesquisa por nome -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="searchLeadName"
                      label="Pesquisar por nome"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      outlined
                      dense
                      @input="filterLeadsByName"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Lista de leads -->
                <v-row v-if="filteredLeadsList.length > 0" dense>
                  <v-col 
                    v-for="(lead, index) in filteredLeadsList" 
                    :key="index" 
                    cols="12" 
                    md="6"
                    lg="4"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-card
                        :elevation="hover ? 8 : 2"
                        :class="{ 'elevation-8': hover }"
                        class="lead-card"
                        outlined
                        @click="selectLeadFromList(lead)"
                        style="cursor: pointer;"
                      >
                        <v-card-text>
                          <div class="d-flex align-center mb-3">
                            <v-avatar
                              size="40"
                              color="primary"
                              class="mr-3"
                            >
                              <v-icon color="white">mdi-account</v-icon>
                            </v-avatar>
                            <div class="flex-grow-1">
                              <div class="text-h6 font-weight-bold primary--text">
                                {{ lead.name || 'Sem nome' }}
                              </div>
                            </div>
                          </div>

                          <v-divider class="my-3"></v-divider>

                          <div class="mb-2">
                            <div class="text-caption grey--text">CPF / CNPJ</div>
                            <div class="text-body-2 font-weight-medium">
                              {{ lead.document || 'Não informado' }}
                            </div>
                          </div>

                          <div class="mb-2" v-if="lead.phone || lead.responsible_phone">
                            <div class="text-caption grey--text">Telefone</div>
                            <div class="text-body-2 font-weight-medium">
                              {{ lead.phone || lead.responsible_phone }}
                            </div>
                          </div>

                          <div class="mb-2" v-if="lead.email">
                            <div class="text-caption grey--text">Email</div>
                            <div class="text-body-2 font-weight-medium">
                              {{ lead.email }}
                            </div>
                          </div>

                          <div v-if="lead.city">
                            <div class="text-caption grey--text">Cidade</div>
                            <div class="text-body-2">
                              {{ lead.city }}{{ lead.uf ? ', ' + lead.uf : '' }}
                            </div>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-hover>
                  </v-col>
                </v-row>

                <v-alert v-else-if="loadingLeadsList" type="info" outlined class="mt-4">
                  <v-icon left>mdi-loading</v-icon>
                  Carregando leads...
                </v-alert>

                <v-alert v-else type="info" outlined class="mt-4">
                  <v-icon left>mdi-information</v-icon>
                  {{ searchLeadName ? 'Nenhum lead encontrado com esse nome.' : 'Nenhum lead cadastrado.' }}
                </v-alert>
              </template>

              <v-divider v-if="showLeadsList" class="my-4"></v-divider>

              <v-row>
                <!-- Documento do Cliente -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="searchDocument"
                    label="CPF / CNPJ"
                    v-mask="searchDocumentMask"
                    @input="handleSearchDocumentInput"
                    :disabled="loading"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <!-- Telefone do Cliente -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="searchPhone"
                    label="Telefone do Cliente"
                    v-mask="'(##) # ####-####'"
                    :disabled="loading"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <!-- Botão Verificar -->
                <v-col cols="12" md="12">
                  <v-btn
                    color="primary"
                    :loading="loading"
                    :disabled="loading || !canSearchLead"
                    @click="checkLead"
                    block
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Verificar Lead
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Visualização de Lead Existente -->
              <template v-if="isEditing && !showClientForm">
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between align-center mb-3">
                  <h3 class="text-h6 primary--text mb-0">
                    <v-icon left color="primary">mdi-account</v-icon>
                    Dados do Cliente
                  </h3>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="editLeadData = true"
                    :disabled="loading"
                  >
                    <v-icon left small>mdi-pencil</v-icon>
                    Editar
                  </v-btn>
                </div>
                
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-card outlined class="pa-3">
                        <div class="text-caption grey--text">CPF / CNPJ </div>
                        <div class="font-weight-medium">{{ leadData.document }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-card outlined class="pa-3">
                        <div class="text-caption grey--text">Razão Social / Nome</div>
                        <div class="font-weight-medium">{{ leadData.name }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-card outlined class="pa-3">
                        <div class="text-caption grey--text">Telefone</div>
                        <div class="font-weight-medium">{{ leadData.phone || 'Não informado' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="4" v-if="isCNPJ">
                      <v-card outlined class="pa-3">
                        <div class="text-caption grey--text">Telefone Responsável</div>
                        <div class="font-weight-medium">{{ leadData.responsible_phone || 'Não informado' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-3">
                        <div class="text-caption grey--text">Email</div>
                        <div class="font-weight-medium">{{ leadData.email || 'Não informado' }}</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-card outlined class="pa-3">
                        <div class="text-caption grey--text">Endereço</div>
                        <div class="font-weight-medium">
                          {{ leadData.address }}, {{ leadData.house_number }}
                          <span v-if="leadData.address_complement"> - {{ leadData.address_complement }}</span><br>
                          {{ leadData.district }}, {{ leadData.city }}/{{ leadData.uf }} - {{ leadData.zip_code }}
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
              </template>

              <!-- Dados do Cliente (Formulário) -->
              <template v-if="showClientForm || editLeadData">
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between align-center mb-3">
                  <h3 class="text-h6 primary--text mb-0">
                    <v-icon left color="primary">mdi-account</v-icon>
                    Dados do Cliente
                  </h3>
                  <v-btn
                    v-if="editLeadData"
                    small
                    outlined
                    color="primary"
                    @click="cancelEdit"
                    :disabled="loading"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Cancelar Edição
                  </v-btn>
                </div>

                <v-form ref="leadForm" v-model="isValidForm" lazy-validation>
                  <v-row>
                    <v-col cols="12" md="8">
                      <v-text-field
                        v-model="leadData.name"
                        label="Razão Social / Nome"
                        :rules="rulesRequired"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                        required
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="leadData.document"
                        label="Documento"
                        v-mask="documentMask"
                        @input="handleDocumentInput"
                        :rules="isEditing ? [] : rulesRequired"
                        :disabled="loading || isBlocked || isEditing || documentLocked"
                        outlined
                        dense
                        :required="!isEditing"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="leadData.phone"
                        label="Telefone"
                        v-mask="'(##) # ####-####'"
                        :rules="[...rulesRequired, ...rulesPhone]"
                        :disabled="loading || isBlocked || phoneLocked"
                        outlined
                        required
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4" v-if="isCNPJ">
                      <v-text-field
                        v-model="leadData.responsible_phone"
                        label="Telefone do Responsável"
                        v-mask="'(##) # ####-####'"
                        :rules="rulesPhone"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="leadData.email"
                        label="Email do Cliente"
                        :rules="[...rulesEmail, ...rulesRequired]"
                        :disabled="loading || isBlocked"
                        outlined
                        required
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="leadData.zip_code"
                        label="CEP"
                        @blur="searchCEP"
                        v-mask="'##.###-###'"
                        :rules="[...rulesRequired, ...rulesCep]"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-select
                        v-model="leadData.uf"
                        :items="states"
                        label="UF"
                        :rules="rulesRequired"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="leadData.address"
                        label="Logradouro"
                        :rules="rulesRequired"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="leadData.house_number"
                        label="Número"
                        :rules="rulesRequired"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="2">
                      <v-text-field
                        v-model="leadData.address_complement"
                        label="Complemento"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="leadData.district"
                        label="Bairro"
                        :rules="rulesRequired"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="leadData.city"
                        label="Cidade"
                        :rules="rulesRequired"
                        :disabled="loading || isBlocked"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-form>

                <!-- Alert para Lead Existente -->

                <!-- Botões de Ação -->
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn
                    v-if="!editLeadData"
                    text
                    @click="resetForm"
                    :disabled="loading"
                  >
                    Limpar
                  </v-btn>
                  <v-btn
                    color="primary"
                    :loading="loading"
                    :disabled="isBlocked || loading"
                    @click="saveLead"
                    :class="editLeadData ? '' : 'ml-3'"
                  >
                    <v-icon left>mdi-content-save</v-icon>
                    {{ editLeadData ? 'Salvar Alterações' : 'Salvar Lead' }}
                  </v-btn>
                </v-col>
              </template>

              <!-- Opções de Proposta - Lead Existente -->
              <template v-if="isEditing && !showClientForm && !editLeadData">
                <v-divider class="my-4"></v-divider>
                <h3 class="text-h6 primary--text mb-4">
                  <v-icon left color="primary">mdi-file-document-edit</v-icon>
                  Proposta
                </h3>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-btn
                      color="primary"
                      block
                      large
                      @click="createNewProposal"
                      class="mb-4"
                    >
                      <v-icon left>mdi-plus-circle</v-icon>
                      Criar Nova Proposta
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-btn
                      color="secondary"
                      block
                      large
                      @click="viewExistingProposals"
                      class="mb-4"
                    >
                      <v-icon left>mdi-file-document-multiple</v-icon>
                      Visualizar Propostas Existentes
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <!-- Opções de Proposta -->
              <template v-if="showProposalOptions">
                <v-divider class="my-4"></v-divider>
                  <h3 class="text-h6 primary--text mb-4">
                    <v-icon left color="primary">mdi-file-document-edit</v-icon>
                    Proposta
                  </h3>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-btn
                      color="primary"
                      block
                      large
                      @click="createNewProposal"
                      class="mb-4"
                    >
                      <v-icon left>mdi-plus-circle</v-icon>
                      Criar Nova Proposta
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-btn
                      color="secondary"
                      block
                      large
                      @click="viewExistingProposals"
                      class="mb-4"
                    >
                      <v-icon left>mdi-file-document-multiple</v-icon>
                      Visualizar Propostas Existentes
                    </v-btn>
                  </v-col>
                </v-row>
              </template>

              <!-- Lista de Propostas Existentes -->
              <template v-if="showProposalsList">
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between align-center mb-4">
                  <h3 class="text-h6 primary--text mb-0">
                    <v-icon left color="primary">mdi-file-document-multiple</v-icon>
                    Propostas Existentes
                  </h3>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    @click="showProposalsList = false"
                    :disabled="loading"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Fechar
                  </v-btn>
                </div>

                <v-row v-if="existingProposals.length > 0" dense>
                  <v-col 
                    style="min-width: 500px;"
                    v-for="(proposal, index) in existingProposals" 
                    :key="index" 
                    cols="12" 
                    md="6"
                    lg="6"
                  >
                    <v-hover v-slot="{ hover }">
                      <v-card
                        :elevation="hover ? 8 : 2"
                        :class="{ 'elevation-8': hover }"
                        class="proposal-card"
                        outlined
                      >
                        <v-card-text>
                          <div class="d-flex align-center mb-3">
                            <v-avatar
                              size="40"
                              color="primary"
                              class="mr-3"
                            >
                              <v-icon color="white">mdi-file-document</v-icon>
                            </v-avatar>
                            <div class="flex-grow-1">
                              <div class="text-h6 font-weight-bold primary--text">
                                {{ getProposalTypeLabel(proposal) }}
                              </div>
                              <div class="mt-1">
                                <v-chip
                                  v-if="proposal.id_diagnostic"
                                  x-small
                                  color="info"
                                  text-color="white"
                                  class="mr-1"
                                >
                                  <v-icon x-small left>mdi-water-outline</v-icon>
                                  Com Waterscan
                                </v-chip>
                                <v-chip
                                  v-else
                                  x-small
                                  color="grey"
                                  text-color="white"
                                  class="mr-1"
                                >
                                  <v-icon x-small left>mdi-water-off</v-icon>
                                  Sem Waterscan
                                </v-chip>
                                <!-- Status de Aceite -->
                                <v-chip
                                  v-if="proposal.status === 'accepted'"
                                  x-small
                                  color="success"
                                  text-color="white"
                                  class="mr-1"
                                >
                                  <v-icon x-small left>mdi-check-circle</v-icon>
                                  Aceita
                                </v-chip>
                                <v-chip
                                  v-else-if="proposal.status === 'billing_requested'"
                                  x-small
                                  color="orange"
                                  text-color="white"
                                  class="mr-1"
                                >
                                  <v-icon x-small left>mdi-clock-alert-outline</v-icon>
                                  Faturamento Solicitado
                                </v-chip>
                                <v-chip
                                  v-else-if="proposal.status === 'billed'"
                                  x-small
                                  color="purple"
                                  text-color="white"
                                  class="mr-1"
                                >
                                  <v-icon x-small left>mdi-receipt</v-icon>
                                  Faturada
                                </v-chip>
                                <v-chip
                                  v-else
                                  x-small
                                  color="orange"
                                  text-color="white"
                                  class="mr-1"
                                >
                                  <v-icon x-small left>mdi-clock-outline</v-icon>
                                  Pendente
                                </v-chip>
                              </div>
                            </div>
                          </div>

                          <v-divider class="my-3"></v-divider>

                          <div class="mb-2">
                            <div class="text-caption grey--text">Valor Total</div>
                            <div class="text-h6 font-weight-bold success--text">
                              {{ formatCurrencyValue(getProposalTotal(proposal)) }}
                            </div>
                          </div>

                          <div class="mb-2" v-if="proposal.createdAt">
                            <div class="text-caption grey--text">
                              <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                              Criada em
                            </div>
                            <div class="text-body-2">
                              {{ formatDate(proposal.createdAt) }}
                            </div>
                          </div>

                          <div v-if="proposal.id_doc">
                            <div class="text-caption grey--text">
                              <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                              ID
                            </div>
                            <div class="text-caption font-weight-medium">
                              {{ proposal.id_doc }}
                            </div>
                          </div>
                        </v-card-text>

                        <v-divider></v-divider>

                        <v-card-actions class="pa-3">
                          <v-btn
                            small
                            color="primary"
                            @click="proposal.id_diagnostic || proposal.status === 'billed' || proposal.status === 'billing_requested' ? viewProposal(proposal) : openProposalForEdit(proposal)"
                            :disabled="loading"
                            class="mr-2"
                            outlined
                          >
                            <v-icon small left>{{ proposal.id_diagnostic ? 'mdi-eye' : (proposal.status === 'billed' || proposal.status === 'billing_requested' ? 'mdi-eye' : 'mdi-pencil') }}</v-icon>
                            {{ proposal.id_diagnostic ? 'Visualizar' : (proposal.status === 'billed' || proposal.status === 'billing_requested' ? 'Visualizar' : 'Editar') }}
                          </v-btn>
                          <!-- Botão de PDF Simplificado -->
                          <v-btn
                            small
                            @click="generateSimplifiedProposalPdf(proposal)"
                            :disabled="loading || generatingPdf"
                            class="mr-2"
                            outlined
                            color="error"
                          >
                            <v-progress-circular
                              v-if="generatingPdf"
                              indeterminate
                              size="16"
                              width="2"
                              color="error"
                              class="mr-1"
                            ></v-progress-circular>
                            <v-icon v-else small left color="error">mdi-file-pdf-box</v-icon>
                            Proposta
                          </v-btn>
                          <!-- Botões de Checklist (apenas para propostas com waterscan) -->
                          <template v-if="proposal.id_diagnostic">
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  small
                                  v-bind="attrs"
                                  v-on="on"
                                  :disabled="loading || idProposalPDF === proposal.id_doc"
                                  class="mr-2"
                                  outlined
                                  color="info"
                                >
                                  <v-progress-circular
                                    v-if="idProposalPDF === proposal.id_doc"
                                    indeterminate
                                    size="16"
                                    width="2"
                                    color="info"
                                    class="mr-1"
                                  ></v-progress-circular>
                                  <v-icon v-else small left color="info">mdi-file-pdf-box</v-icon>
                                  Checklists
                                </v-btn>
                              </template>
                              <v-list dense>
                                <v-list-item
                                  v-for="(menuItem, index) in itemsMenuPDF"
                                  :key="index"
                                  @click="pdfRequest(proposal, menuItem.value)"
                                  :disabled="loading || idProposalPDF === proposal.id_doc"
                                >
                                  <v-list-item-icon>
                                    <v-icon small>mdi-file-pdf-box</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    <v-list-item-title>{{ menuItem.title }}</v-list-item-title>
                                  </v-list-item-content>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </template>
                          <v-spacer></v-spacer>
                          <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                small
                                icon
                                v-bind="attrs"
                                v-on="on"
                                :disabled="loading"
                              >
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list dense>
                              <v-list-item
                                v-if="proposal.id_diagnostic"
                                @click="openWaterscanFromProposal(proposal)"
                                :disabled="loading"
                              >
                                <v-list-item-icon>
                                  <v-icon small>mdi-water-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title>Ver Waterscan</v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                              <!-- Aceitar Proposta -->
                              <v-list-item
                                v-if="proposal.status === 'pending'"
                                @click="acceptProposal(proposal)"
                                :disabled="loading"
                              >
                                <v-list-item-icon>
                                  <v-icon small color="success">mdi-check-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title>Aceitar Proposta</v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                              <!-- Gerar Ordem de Faturamento -->
                              <v-list-item
                                v-if="proposal.status === 'accepted' && !proposal.billingOrderId"
                                @click="generateBillingOrder(proposal)"
                                :disabled="loading || generatingBillingOrder || proposal.status === 'billing_requested' || proposal.status === 'billed'"
                              >
                                <v-list-item-icon>
                                  <v-progress-circular
                                    v-if="generatingBillingOrder"
                                    indeterminate
                                    size="16"
                                    width="2"
                                    color="primary"
                                  ></v-progress-circular>
                                  <v-icon v-else small color="purple">mdi-receipt</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title>
                                    Gerar Ordem de Faturamento
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                              <!-- Ver Ordem de Faturamento -->
                              <v-list-item
                                v-if="proposal.billingOrderId"
                                @click="viewBillingOrder(proposal)"
                                :disabled="loading"
                              >
                                <v-list-item-icon>
                                  <v-icon small color="purple">mdi-receipt-text</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                  <v-list-item-title>
                                    Ver Ordem de Faturamento
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-card-actions>
                      </v-card>
                    </v-hover>
                  </v-col>
                </v-row>

                <v-alert v-else type="info" outlined class="mt-4">
                  <v-icon left>mdi-information</v-icon>
                  Nenhuma proposta encontrada para este lead.
                </v-alert>
              </template>

            <!-- Lista de Waterscans (Diagnósticos) -->
            <template v-if="showProposalsList">
              <v-divider class="my-6"></v-divider>
              <div class="d-flex justify-space-between align-center mb-4">
                <h3 class="text-h6 primary--text mb-0">
                  <v-icon left color="primary">mdi-water-outline</v-icon>
                  Waterscans (Diagnósticos) do Cliente
                </h3>
              </div>

              <v-row v-if="existingDiagnostics.length > 0" dense>
                <v-col 
                  v-for="(diag, index) in existingDiagnostics" 
                  :key="index" 
                  cols="12" 
                  md="6"
                  lg="4"
                >
                  <v-hover v-slot="{ hover }">
                    <v-card
                      :elevation="hover ? 8 : 2"
                      :class="{ 'elevation-8': hover }"
                      class="waterscan-card"
                      outlined
                    >
                      <v-card-text>
                        <div class="d-flex align-center mb-3">
                          <v-avatar
                            size="40"
                            color="info"
                            class="mr-3"
                          >
                            <v-icon color="white">mdi-water-outline</v-icon>
                          </v-avatar>
                          <div class="flex-grow-1">
                            <div class="text-h6 font-weight-bold info--text">
                              Waterscan #{{ index + 1 }}
                            </div>
                          </div>
                        </div>

                        <v-divider class="my-3"></v-divider>

                        <div class="mb-2" v-if="diag.createdAt">
                          <div class="text-caption grey--text">
                            <v-icon x-small class="mr-1">mdi-calendar-plus</v-icon>
                            Criado em
                          </div>
                          <div class="text-body-2">
                            {{ formatDate(diag.createdAt) }}
                          </div>
                        </div>

                        <div class="mb-2" v-if="diag.lastUpdateAt">
                          <div class="text-caption grey--text">
                            <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                            Última atualização
                          </div>
                          <div class="text-body-2">
                            {{ formatDateFromTimestamp(diag.lastUpdateAt) }}
                          </div>
                        </div>

                        <div v-if="diag.id_doc">
                          <div class="text-caption grey--text">
                            <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                            ID
                          </div>
                          <div class="text-caption font-weight-medium">
                            {{ diag.id_doc }}
                          </div>
                        </div>
                      </v-card-text>

                      <v-divider></v-divider>

                      <v-card-actions class="pa-3">
                        <v-btn
                          small
                          color="primary"
                          @click="openExistingDiagnostic(diag)"
                          :disabled="loading"
                          class="mr-2"
                          outlined
                        >
                          <v-icon small left>mdi-open-in-new</v-icon>
                          Abrir
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                          small
                          color="success"
                          @click="createProposalFromWaterscan(diag)"
                          :disabled="loading"
                        >
                          <v-icon small left>mdi-file-document-plus</v-icon>
                          Criar Proposta
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-hover>
                </v-col>
              </v-row>

              <v-alert v-else type="info" outlined class="mt-4">
                <v-icon left>mdi-information</v-icon>
                Nenhum waterscan encontrado para este lead.
              </v-alert>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo de escolha do modo de criação -->
    <v-dialog v-model="showProposalModeDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">
          Como deseja criar a proposta?
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="proposalMode === 'without-waterscan'"
            type="warning"
            prominent
            border="left"
            class="mb-4"
          >
            <strong>Atenção:</strong> O WaterScan é essencial para demonstração da eficiência hídrica ao cliente e facilita no fechamento. Recomendamos fortemente criar a proposta com WaterScan.
          </v-alert>

          <v-radio-group v-model="proposalMode" column>
            <v-radio
              value="with-waterscan"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-bold">Com WaterScan</div>
                  <div class="text-caption grey--text">Recomendado - Demonstra eficiência hídrica</div>
                </div>
              </template>
            </v-radio>
            <v-radio
              v-if="!isWaterscanRequired"
              value="without-waterscan"
            >
              <template v-slot:label>
                <div>
                  <div class="font-weight-bold">Sem WaterScan</div>
                  <div class="text-caption grey--text">Não recomendado</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showProposalModeDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="proceedWithProposal">
            Continuar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Download de PDF -->
    <v-dialog v-model="dialogDownload" max-width="400">
      <v-card>
        <v-card-title class="headline">Salvar Arquivo</v-card-title>
        <v-card-text>
          Clique abaixo para salvar o arquivo.
          <br />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <a :href="fileLink" download>
            <v-btn color="success" text @click="dialogDownload = false">
              Salvar Arquivo
            </v-btn>
          </a>
          <v-btn color="error" text @click="dialogDownload = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para mensagens -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Diagnostic Component -->
    <Diagnostic
      v-if="openDiagnostic"
      :openModal="openDiagnostic"
      :idLead="diagnosticProps.idLead"
      :idDiagnostic="diagnosticProps.idDiagnostic"
      :closeFunc="closeDiagnostic"
      :currentStep="diagnosticProps.currentStep"
      :initialTab="diagnosticProps.currentStep === 'proposal' ? 'proposal' : 'checklist'"
      :proposalType="'kit_sell'"
      @saved="handleDiagnosticSaved"
    />
    
    <!-- Proposal Component (Standalone) -->
    <ProposalDialog
      v-if="openProposal"
      :openModal="openProposal"
      :idLead="diagnosticProps.idLead"
      :idFranchisee="currentUserFranchiseeId"
      :proposalId="selectedProposalId"
      :param="selectedDiagnostic"
      :fixedProposalType="'kit_sell'"
      :readonly="selectedProposalStatus === 'billed' || selectedProposalStatus === 'billing_requested'"
      :closeFunc="closeProposal"
    />

    <!-- Billing Order Dialog -->
    <BillingOrderDialog
      v-if="openBillingOrderDialog"
      :openModal="openBillingOrderDialog"
      :proposal="selectedProposalForBilling"
      :leadData="billingOrderLeadData"
      :total="getProposalTotal(selectedProposalForBilling)"
      @close="closeBillingOrderDialog"
      @confirm="confirmBillingOrder"
    />

    <!-- Billing Order View Dialog -->
    <BillingOrderViewDialog
      :openModal="openBillingOrderViewDialog"
      :billingOrderId="selectedBillingOrderId"
      @close="closeBillingOrderViewDialog"
    />

    <!-- Modal de Visualização Simplificada da Proposta -->
    <v-dialog
      v-model="showProposalModal"
      max-width="800px"
      scrollable
    >
      <v-card v-if="selectedProposalForView">
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">mdi-file-document-outline</v-icon>
          Visualização da Proposta
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Informações Principais -->
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">ID da Proposta</div>
                <div class="info-value">{{ selectedProposalForView.id_doc || 'N/A' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Tipo de Proposta</div>
                <div class="info-value">{{ getProposalTypeLabel(selectedProposalForView) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Status</div>
                <div class="info-value">
                  <v-chip
                    small
                    :color="getStatusColor(selectedProposalForView.status)"
                    text-color="white"
                  >
                    {{ getStatusLabel(selectedProposalForView.status) }}
                  </v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item mb-3">
                <div class="info-label">Data de Criação</div>
                <div class="info-value">{{ formatDate(selectedProposalForView.createdAt) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedProposalForView.acceptedAt">
              <div class="info-item mb-3">
                <div class="info-label">Data de Aceitação</div>
                <div class="info-value">{{ formatDate(selectedProposalForView.acceptedAt) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedProposalForView.billedAt">
              <div class="info-item mb-3">
                <div class="info-label">Data de Faturamento</div>
                <div class="info-value">{{ formatDate(selectedProposalForView.billedAt) }}</div>
              </div>
            </v-col>
            <v-col cols="12">
              <v-row dense>
                <v-col cols="12" md="4" v-if="getProposalDiscountAmount(selectedProposalForView) > 0">
                  <div class="info-item mb-3">
                    <div class="info-label">Subtotal</div>
                    <div class="info-value">
                      {{ formatCurrencyValue(getProposalSubtotal(selectedProposalForView)) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" v-if="getProposalDiscountAmount(selectedProposalForView) > 0">
                  <div class="info-item mb-3">
                    <div class="info-label">Desconto</div>
                    <div class="info-value success--text font-weight-bold">
                      - {{ formatCurrencyValue(getProposalDiscountAmount(selectedProposalForView)) }}
                    </div>
                  </div>
                </v-col>
                <v-col :cols="12" :md="getProposalDiscountAmount(selectedProposalForView) > 0 ? 4 : 12">
                  <div class="info-item mb-3">
                    <div class="info-label">Valor Total</div>
                    <div class="info-value font-weight-bold text-h6 primary--text">
                      {{ getProposalTotalFormatted(selectedProposalForView) }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Lista de Itens -->
          <v-divider class="my-4"></v-divider>
          <div v-if="getProposalItems(selectedProposalForView).length > 0">
            <div class="text-h6 mb-3">Itens da Proposta</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nome</th>
                  <th class="text-right">Quantidade</th>
                  <th class="text-right">Valor Unitário</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in getProposalItems(selectedProposalForView)" :key="index">
                  <td>
                    <v-chip x-small :color="item.type === 'Solução' ? 'purple' : item.type === 'Produto' ? 'blue' : 'green'" text-color="white">
                      {{ item.type }}
                    </v-chip>
                  </td>
                  <td>{{ item.name }}</td>
                  <td class="text-right">{{ item.quantity }}</td>
                  <td class="text-right">{{ item.unitPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</td>
                  <td class="text-right font-weight-medium">{{ item.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeProposalModal">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import LeadsServiceFirebase from "@/services/firebase/Leads/LeadsService.js";
import { showErrorSnackBar, showSuccessSnackBar } from "@/utils/SnackBartHelper";
import Utils from '@/scripts/Utils';
import Rules from '@/utils/Rules';
import Diagnostic from "@/components/ProjectsKits/Diagnostic/Diagnostic.vue";
import ProposalDialog from "@/components/ProjectsKits/Proposal/ProposalDialog.vue";
import BillingOrderDialog from "@/components/ProjectsKits/BillingOrder/BillingOrderDialog.vue";
import BillingOrderViewDialog from "@/components/ProjectsKits/BillingOrder/BillingOrderViewDialog.vue";
import DiagnosticProposalService from '@/services/firebase/DiagnosticProposal/DiagnosticProposalService';
import DiagnosticServiceFirebase from '@/services/firebase/Diagnostic/DiagnosticService';
import WaterScanApi from '@/services/apiPdf/WaterScanApi';
import BillingOrderService from '@/services/firebase/BillingOrder/BillingOrderService';
import BillingOrderApiService from '@/services/server/BillingOrderService';
import SolutionsServices from '@/services/firebase/SolutionsServices/SolutionsServices';

// Configuração: Define se o Waterscan é obrigatório para criar propostas
// Para liberar propostas sem Waterscan no futuro, altere este valor para false
const WATERSCAN_REQUIRED = true;

export default {
  name: 'ProjectsKits',
  
  components: {
    Diagnostic,
    ProposalDialog,
    BillingOrderDialog,
    BillingOrderViewDialog
  },
  
  data: () => ({
    isValidForm: true,
    loading: false,
    isBlocked: false,
    existingLead: null,
    isEditing: false,
    showClientForm: false,
    documentLocked: false,
    phoneLocked: false,
    showProposalOptions: false,
    showProposalModeDialog: false,
    editLeadData: false,
    savedLeadId: null,
    proposalMode: null, // 'with-waterscan', 'without-waterscan', null
    isWaterscanRequired: WATERSCAN_REQUIRED, // Flag para controlar se Waterscan é obrigatório
    openDiagnostic: false,
    openProposal: false,
    showProposalsList: false,
    existingProposals: [],
    existingDiagnostics: [],
    selectedProposalId: null,
    selectedDiagnostic: null,
    selectedProposalStatus: null,
    generatingPdf: false,
    generatingBillingOrder: false,
    openBillingOrderDialog: false,
    openBillingOrderViewDialog: false,
    selectedProposalForBilling: null,
    dialogDownload: false,
    fileLink: null,
    idProposalPDF: null,
    showProposalModal: false,
    selectedProposalForView: null,
    solutionsData: [],
    itemsMenuPDF: [
      {
        value: 'checklist',
        title: 'Checklist Simples',
        permission_name: 'waterscan_simple_checklist_report',
      },
      {
        value: 'checklist_with_photo',
        title: 'Checklist Com Foto',
        permission_name: 'waterscan_photo_checklist_report',
      },
    ],
    billingOrderLeadData: null,
    selectedBillingOrderId: null,
    diagnosticProps: {
      idLead: null,
      idProject: null,
      idDiagnostic: null,
      currentStep: 'diagnostic'
    },
    states: [
      'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
      'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ],
    searchDocument: null,
    searchPhone: null,
    searchDocumentMask: "###.###.###-##",
    leadData: {
      document: null,
      name: null,
      phone: null,
      responsible_phone: null,
      email: null,
      zip_code: null,
      uf: null,
      address: null,
      house_number: null,
      address_complement: null,
      district: null,
      city: null,
      id_franchisee: null,
    },
    documentMask: "###.###.###-##",
    snackbar: {
      show: false,
      message: '',
      color: 'info'
    },
    showLeadsList: false,
    leadsList: [],
    searchLeadName: '',
    loadingLeadsList: false,
  }),

  computed: {
    ...mapGetters([
      'getCurrentUserData',
      'getCurrentUserRank'
    ]),
    rulesRequired() {
      return Rules.required();
    },
    rulesPhone() {
      return Rules.string(true, 16);
    },
    rulesEmail() {
      return Rules.email();
    },
    rulesCep() {
      return Rules.cep(true);
    },
    rulesString() {
      return Rules.string(true, 64);
    },
    currentUserId() {
      return this.getCurrentUserData?.id || null;
    },

 

    currentUserFranchiseeId() {
      return this.getCurrentUserData?.id || null;
    },

    canSearchLead() {
      // Se tiver telefone, verificar se está completo (11 dígitos)
      if (this.searchPhone) {
        const phoneDigitsOnly = this.searchPhone.replace(/\D/g, '');
        // Telefone completo tem 11 dígitos (DDD + número)
        if (phoneDigitsOnly.length === 11) {
          return true;
        }
      }
      
      // Se tiver documento, verificar se está completo
      if (this.searchDocument) {
        const digitsOnly = this.searchDocument.replace(/\D/g, '');
        // CPF completo tem 11 dígitos, CNPJ completo tem 14 dígitos
        return digitsOnly.length === 11 || digitsOnly.length === 14;
      }
      
      return false;
    },
    filteredLeadsList() {
      if (!this.searchLeadName || this.searchLeadName.trim() === '') {
        return this.leadsList;
      }
      
      const searchTerm = this.searchLeadName.toLowerCase().trim();
      return this.leadsList.filter(lead => {
        const name = (lead.name || '').toLowerCase();
        return name.includes(searchTerm);
      });
    },
    isCNPJ() {
      if (!this.leadData.document) return false;
      const digitsOnly = this.leadData.document.replace(/\D/g, '');
      return digitsOnly.length === 14;
    }
  },

  watch: {
    'searchDocument'(newVal) {
      if (newVal) {
        const digitsOnly = newVal.replace(/\D/g, '');
        if (digitsOnly.length <= 11) {
          this.searchDocumentMask = "###.###.###-##";
        } else {
          this.searchDocumentMask = "##.###.###/####-##";
        }
      }
    },
    'leadData.document'(newVal) {
      if (newVal) {
        const digitsOnly = newVal.replace(/\D/g, '');
        if (digitsOnly.length <= 11) {
          this.documentMask = "###.###.###-##";
        } else {
          this.documentMask = "##.###.###/####-##";
        }
      }
    },
    'showLeadsList'(newVal) {
      if (newVal && this.leadsList.length === 0) {
        this.loadLeadsList();
      }
    }
  },

  methods: {
    openExistingDiagnostic(diagnostic) {
      if (!diagnostic || !diagnostic.id_doc) return;
      console.log(">>>>>>>> OPEN EXISTING DIAGNOSTIC <<<<<<<<", diagnostic)
      // Abrir usando o mesmo componente Diagnostic (modal), não rota
      const leadId = this.savedLeadId || (this.existingLead && this.existingLead.id_doc) || diagnostic.id_lead || null;
      this.diagnosticProps.idLead = leadId;
      this.diagnosticProps.idDiagnostic = diagnostic.id_doc;
      this.diagnosticProps.currentStep = 'diagnostic';
      this.openDiagnostic = true;
    },

    openWaterscanFromProposal(proposal) {
      if (!proposal || !proposal.id_diagnostic) {
        this.showSnackbar('Esta proposta não possui waterscan vinculado', 'warning');
        return;
      }
      
      // Abrir o waterscan usando o id_diagnostic da proposta
      const leadId = proposal.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc) || null;
      this.diagnosticProps.idLead = leadId;
      this.diagnosticProps.idDiagnostic = proposal.id_diagnostic;
      this.diagnosticProps.currentStep = 'diagnostic';
      this.openDiagnostic = true;
    },

    async checkLead() {
      if (!this.canSearchLead) {
        if (this.searchPhone) {
          const phoneDigitsOnly = this.searchPhone.replace(/\D/g, '');
          if (phoneDigitsOnly.length < 11) {
            this.showSnackbar('Por favor, digite o telefone completo (11 dígitos)', 'warning');
            return;
          }
        }
        
        if (this.searchDocument) {
          const digitsOnly = this.searchDocument.replace(/\D/g, '');
          if (digitsOnly.length < 11) {
            this.showSnackbar('Por favor, digite o CPF completo (11 dígitos)', 'warning');
          } else if (digitsOnly.length > 11 && digitsOnly.length < 14) {
            this.showSnackbar('Por favor, digite o CNPJ completo (14 dígitos)', 'warning');
          } else {
            this.showSnackbar('Por favor, informe o documento completo ou telefone completo', 'warning');
          }
        } else {
          this.showSnackbar('Por favor, informe o documento completo ou telefone completo', 'warning');
        }
        return;
      }

      this.loading = true;
      this.isBlocked = false;
      this.existingLead = null;
      this.showClientForm = false;
      this.documentLocked = false;
      this.phoneLocked = false;

      // Limpar dados do formulário antes de verificar
      this.leadData = {
        document: null,
        name: null,
        phone: null,
        responsible_phone: null,
        email: null,
        zip_code: null,
        uf: null,
        address: null,
        house_number: null,
        address_complement: null,
        district: null,
        city: null,
        id_franchisee: null,
      };
      this.documentMask = "###.###.###-##";
      this.isEditing = false;
      this.editLeadData = false;
      this.showProposalOptions = false;
      this.showProposalsList = false;
      this.existingProposals = [];
      this.existingDiagnostics = [];

      try {
        let result;
        
        if (this.searchDocument) {
          // Verificar novamente se o documento está completo antes de buscar
          const digitsOnly = this.searchDocument.replace(/\D/g, '');
          if (digitsOnly.length !== 11 && digitsOnly.length !== 14) {
            this.showSnackbar('Por favor, digite o documento completo', 'warning');
            this.loading = false;
            return;
          }
          
          result = await LeadsServiceFirebase.searchLeadByDocumentAndFranchisee(
            this.searchDocument,
            this.currentUserFranchiseeId
          );
        } else if (this.searchPhone) {
          // Verificar novamente se o telefone está completo antes de buscar
          const phoneDigitsOnly = this.searchPhone.replace(/\D/g, '');
          if (phoneDigitsOnly.length !== 11) {
            this.showSnackbar('Por favor, digite o telefone completo (11 dígitos)', 'warning');
            this.loading = false;
            return;
          }
          
          // Normalize phone format to match database format
          const normalizedPhone = this.normalizePhone(this.searchPhone);
          result = await LeadsServiceFirebase.searchLeadByPhoneAndFranchisee(
            normalizedPhone,
            this.currentUserFranchiseeId
          );
        }

        if (result === null) {
          // Lead não existe ou não está vinculado a nenhum franqueado
          this.showClientForm = true;
          this.isEditing = false;
          this.leadData.id_franchisee = this.currentUserFranchiseeId;
          
          // Copiar dados de pesquisa para o formulário de edição
          if (this.searchDocument) {
            this.leadData.document = this.searchDocument;
            this.documentLocked = true; // Bloquear edição do documento após busca
          }
          
          // If searching by phone, move phone to phone field
          if (this.searchPhone && !this.searchDocument) {
            this.leadData.phone = this.normalizePhone(this.searchPhone);
            this.phoneLocked = true; // Bloquear edição do telefone após busca
          }
          
          this.showSnackbar('Lead disponível para cadastro', 'success');
        } else if (result === 'exists_other_franchisee') {
          // Lead existe para outro franqueado
          this.isBlocked = true;
          this.showClientForm = false; // Não abrir o formulário
          this.showSnackbar('Este lead já está cadastrado para outro franqueado', 'error');
        } else {
          // Lead existe para este franqueado
          this.existingLead = result;
          this.populateFormFromExistingLead(result);
          this.isEditing = true;
          this.showClientForm = false; // Não mostrar formulário, apenas visualização
          this.savedLeadId = result.id_doc;
          this.showSnackbar('Lead encontrado', 'info');
        }
      } catch (error) {
        console.error('Erro ao verificar lead:', error);
        this.showSnackbar('Erro ao verificar lead', 'error');
      } finally {
        this.loading = false;
      }
    },

    populateFormFromExistingLead(lead) {
      this.leadData.document = lead.document || null;
      this.leadData.name = lead.name || null;
      this.leadData.phone = lead.phone || null;
      this.leadData.responsible_phone = lead.responsible_phone || null;
      this.leadData.email = lead.email || null;
      this.leadData.zip_code = lead.zip_code || null;
      this.leadData.uf = lead.uf || null;
      this.leadData.address = lead.address || null;
      this.leadData.house_number = lead.house_number || null;
      this.leadData.address_complement = lead.address_complement || null;
      this.leadData.district = lead.district || null;
      this.leadData.city = lead.city || null;
      this.leadData.id_franchisee = lead.id_franchisee || this.currentUserFranchiseeId;
      
      // Atualizar máscara baseada no documento carregado
      if (this.leadData.document) {
        const digitsOnly = this.leadData.document.replace(/\D/g, '');
        if (digitsOnly.length <= 11) {
          this.documentMask = "###.###.###-##";
        } else {
          this.documentMask = "##.###.###/####-##";
        }
      } else {
        this.documentMask = "###.###.###-##";
      }
    },

    async searchCEP() {
      if (!this.leadData.zip_code || this.leadData.zip_code.length !== 10) {
        return;
      }

      const cep = this.leadData.zip_code.replace(/\D/g, '');
      
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          this.leadData.address = data.logradouro || '';
          this.leadData.district = data.bairro || '';
          this.leadData.city = data.localidade || '';
          this.leadData.uf = data.uf || '';
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    },

    async saveLead() {
      if (this.$refs.leadForm && !this.$refs.leadForm.validate()) {
        this.showSnackbar('Preencha todos os campos obrigatórios', 'warning');
        return;
      }

      // Check if creating a new lead and document is required
      if (!this.existingLead && !this.leadData.document) {
        this.showSnackbar('Por favor, informe o documento', 'warning');
        return;
      }

      // Verificar se o lead já está vinculado a outro franqueado antes de salvar
      this.loading = true;
      try {
        // Verificar por documento se estiver criando novo lead ou se o documento mudou
        if (this.leadData.document && (!this.existingLead || this.existingLead.document !== this.leadData.document)) {
          const documentCheck = await LeadsServiceFirebase.searchLeadByDocumentAndFranchisee(
            this.leadData.document,
            this.currentUserFranchiseeId
          );
          
          if (documentCheck === 'exists_other_franchisee') {
            this.loading = false;
            this.showSnackbar('Este documento já está cadastrado para outro franqueado', 'error');
            return;
          }
          
          // Se o documento existe para este franqueado mas não é o lead atual sendo editado
          if (documentCheck && documentCheck !== null && documentCheck !== 'exists_other_franchisee') {
            if (!this.existingLead || documentCheck.id_doc !== this.existingLead.id_doc) {
              this.loading = false;
              this.showSnackbar('Este documento já está cadastrado para este franqueado', 'warning');
              return;
            }
          }
        }

        // Verificar por telefone se estiver criando novo lead ou se o telefone mudou
        if (this.leadData.phone) {
          const normalizedPhone = this.normalizePhone(this.leadData.phone);
          const phoneCheck = await LeadsServiceFirebase.searchLeadByPhoneAndFranchisee(
            normalizedPhone,
            this.currentUserFranchiseeId
          );
          
          if (phoneCheck === 'exists_other_franchisee') {
            this.loading = false;
            this.showSnackbar('Este telefone já está cadastrado para outro franqueado', 'error');
            return;
          }
          
          // Se o telefone existe para este franqueado mas não é o lead atual sendo editado
          if (phoneCheck && phoneCheck !== null && phoneCheck !== 'exists_other_franchisee') {
            if (!this.existingLead || phoneCheck.id_doc !== this.existingLead.id_doc) {
              this.loading = false;
              this.showSnackbar('Este telefone já está cadastrado para este franqueado', 'warning');
              return;
            }
          }
        }

        // Verificar por email se estiver criando novo lead ou se o email mudou
        if (this.leadData.email && this.leadData.email.trim()) {
          const emailCheck = await LeadsServiceFirebase.searchLeadByEmailAndFranchisee(
            this.leadData.email.trim(),
            this.currentUserFranchiseeId
          );
          
          if (emailCheck === 'exists_other_franchisee') {
            this.loading = false;
            this.showSnackbar('Este email já está cadastrado para outro franqueado', 'error');
            return;
          }
          
          // Se o email existe para este franqueado mas não é o lead atual sendo editado
          if (emailCheck && emailCheck !== null && emailCheck !== 'exists_other_franchisee') {
            if (!this.existingLead || emailCheck.id_doc !== this.existingLead.id_doc) {
              this.loading = false;
              this.showSnackbar('Este email já está cadastrado para este franqueado', 'warning');
              return;
            }
          }
        }

        const leadToSave = {
          ...this.leadData,
          document: this.leadData.document,
          name: this.leadData.name,
          phone: this.leadData.phone ? this.normalizePhone(this.leadData.phone) : null,
          responsible_phone: this.leadData.responsible_phone ? this.normalizePhone(this.leadData.responsible_phone) : null,
          email: this.leadData.email || null,
          zip_code: this.leadData.zip_code,
          uf: this.leadData.uf,
          address: this.leadData.address,
          house_number: this.leadData.house_number,
          address_complement: this.leadData.address_complement || null,
          district: this.leadData.district,
          city: this.leadData.city,
          id_franchisee: this.currentUserFranchiseeId,
          created_at: new Date(),
          updated_at: new Date(),
        };

        let result;
        if (this.existingLead && !this.isBlocked) {
          // Atualizar lead existente
          await LeadsServiceFirebase.editLead(this.existingLead.id_doc, leadToSave);
          result = this.existingLead.id_doc;
        } else {
          // Criar novo lead
          result = await LeadsServiceFirebase.createLead(leadToSave);
        }

      showSuccessSnackBar('Lead salvo com sucesso!');
      this.savedLeadId = result;
      
      if (this.editLeadData) {
        // Se estava editando, voltar para visualização
        this.editLeadData = false;
        this.showClientForm = false;
      } else {
        // Se era novo lead, mostrar opções de proposta
        this.showClientForm = false;
        this.showProposalOptions = true;
      }
      } catch (error) {
        console.error('Erro ao salvar lead:', error);
        showErrorSnackBar('Erro ao salvar lead');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.searchDocument = null;
      this.searchPhone = null;
      this.searchDocumentMask = "###.###.###-##";
      this.leadData = {
        document: null,
        name: null,
        phone: null,
        responsible_phone: null,
        email: null,
        zip_code: null,
        uf: null,
        address: null,
        house_number: null,
        address_complement: null,
        district: null,
        city: null,
        id_franchisee: null,
      };
      this.documentMask = "###.###.###-##";
      this.typeDocument = 'CPF';
      this.isBlocked = false;
      this.existingLead = null;
      this.isEditing = false;
      this.showClientForm = false;
      this.documentLocked = false;
      this.phoneLocked = false;
      this.showProposalOptions = false;
      this.showProposalModeDialog = false;
      this.savedLeadId = null;
      this.proposalMode = null;
      this.editLeadData = false;
      this.openDiagnostic = false;
      this.openProposal = false;
      this.showProposalsList = false;
      this.existingProposals = [];
      this.existingDiagnostics = [];
      this.selectedProposalId = null;
      this.selectedDiagnostic = null;
      this.selectedProposalStatus = null;
      this.diagnosticProps = {
        idLead: null,
        idProject: null,
        idDiagnostic: null,
        currentStep: 'diagnostic'
      };
      if (this.$refs.leadForm) {
        this.$refs.leadForm.resetValidation();
      }
    },

    showSnackbar(message, color = 'info') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    normalizePhone(phone) {
      if (!phone) return phone;
      // Remove all non-digit characters
      const digitsOnly = phone.replace(/\D/g, '');
      
      // Format as (XX) X XXXX-XXXX
      if (digitsOnly.length === 11) {
        return `(${digitsOnly.substring(0, 2)}) ${digitsOnly.substring(2, 3)} ${digitsOnly.substring(3, 7)}-${digitsOnly.substring(7)}`;
      }
      
      // If already formatted, return as is
      return phone;
    },

    handleSearchDocumentInput() {
      if (!this.searchDocument) {
        this.searchDocumentMask = "###.###.###-##";
        return;
      }
      
      const digitsOnly = this.searchDocument.replace(/\D/g, '');
      if (digitsOnly.length <= 11) {
        this.searchDocumentMask = "###.###.###-##";
      } else {
        this.searchDocumentMask = "##.###.###/####-##";
      }
    },

    handleDocumentInput() {
      if (!this.leadData.document) {
        this.documentMask = "###.###.###-##";
        return;
      }
      
      const digitsOnly = this.leadData.document.replace(/\D/g, '');
      if (digitsOnly.length <= 11) {
        this.documentMask = "###.###.###-##";
      } else {
        this.documentMask = "##.###.###/####-##";
      }
    },

    createNewProposal() {
      // Se Waterscan é obrigatório, definir automaticamente e pular o diálogo
      if (WATERSCAN_REQUIRED) {
        this.proposalMode = 'with-waterscan';
        this.proceedWithProposal();
        return;
      }
      
      // Caso contrário, mostrar diálogo de escolha
      this.proposalMode = null; // Reset selection
      this.showProposalModeDialog = true;
    },

    async viewExistingProposals() {
      // Get lead ID
      const leadId = this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
      
      if (!leadId) {
        this.showSnackbar('Lead não encontrado', 'error');
        return;
      }

      // Get franchisee ID
      const franchiseeId = this.currentUserFranchiseeId;
      
      if (!franchiseeId) {
        this.showSnackbar('Franqueado não identificado', 'error');
        return;
      }

      this.loading = true;
      
      try {
        // Buscar propostas existentes para esse lead e franqueado
        const proposals = await DiagnosticProposalService.getProposalsByFranchiseeIdAndLeadId(
          franchiseeId,
          leadId
        );

        // Buscar diagnostics (waterscans) existentes para esse lead e franqueado
        const diagnostics = await DiagnosticServiceFirebase.getDiagnosticsByLeadAndFranchisee(
          leadId,
          franchiseeId
        );

        if (!proposals || proposals.length === 0) {
          this.showSnackbar('Nenhuma proposta encontrada para este lead', 'info');
          this.existingProposals = [];
        } else {
          // Exibir lista de propostas na mesma página
          // Garantir que propostas antigas tenham status padrão
        this.existingProposals = proposals.map(proposal => ({
          ...proposal,
          status: proposal.status || 'pending',
          acceptedAt: proposal.acceptedAt || null,
          billedAt: proposal.billedAt || null,
          billingOrderId: proposal.billingOrderId || null
        }));

        // Ordenar por data de criação (mais nova primeiro)
        this.existingProposals.sort((a, b) => {
          const dateA = a.createdAt?._seconds 
            ? new Date(a.createdAt._seconds * 1000)
            : a.createdAt?.toDate 
            ? a.createdAt.toDate()
            : new Date(a.createdAt || 0);
          const dateB = b.createdAt?._seconds 
            ? new Date(b.createdAt._seconds * 1000)
            : b.createdAt?.toDate 
            ? b.createdAt.toDate()
            : new Date(b.createdAt || 0);
          return dateB - dateA; // Ordem decrescente (mais nova primeiro)
        });
        }

        // Sempre que buscar, exibir a seção; se não houver itens, mostra alerta informativo
        this.existingDiagnostics = diagnostics || [];
        this.showProposalsList = true;
      } catch (error) {
        console.error('Erro ao buscar propostas:', error);
        this.showSnackbar('Erro ao buscar propostas existentes', 'error');
        this.showProposalsList = false;
        this.existingProposals = [];
        this.existingDiagnostics = [];
      } finally {
        this.loading = false;
      }
    },

    async proceedWithProposal() {
      // Se Waterscan é obrigatório, garantir que está selecionado
      if (WATERSCAN_REQUIRED) {
        this.proposalMode = 'with-waterscan';
      }
      
      if (!this.proposalMode) {
        this.showSnackbar('Por favor, selecione um modo de criação', 'warning');
        return;
      }

      this.showProposalModeDialog = false;

      // Use savedLeadId or existingLead.id_doc
      const leadId = this.savedLeadId || (this.existingLead && this.existingLead.id_doc);

      if (this.proposalMode === 'with-waterscan') {
        // Create project and open Diagnostic component
        this.loading = true;
        try {
          await this.createProjectAndOpenDiagnostic(leadId);
        } catch (error) {
          console.error('Error creating project:', error);
          showErrorSnackBar('Erro ao criar projeto');
        } finally {
          this.loading = false;
        }
      } else if (this.proposalMode === 'without-waterscan') {
        // Open Proposal component standalone (without waterscan)
        this.diagnosticProps.idLead = leadId;
        this.diagnosticProps.idProject = null; // No project needed for proposal without waterscan
        this.diagnosticProps.idDiagnostic = null;
        this.diagnosticProps.currentStep = 'proposal';
        this.openProposal = true;
      }

      if (this.showProposalOptions) {
        this.showProposalOptions = false;
      }
    },

    async createProjectAndOpenDiagnostic(leadId) {
      try {
        
        // Set diagnostic props
        this.diagnosticProps.idLead = leadId;
        this.diagnosticProps.idDiagnostic = null; // New diagnostic
        this.diagnosticProps.currentStep = 'diagnostic';
        
        // Open Diagnostic component
        this.openDiagnostic = true;
        
      } catch (error) {
        console.error('Error creating project:', error);
        showErrorSnackBar('Erro ao criar projeto');
        throw error;
      }
    },

    async closeDiagnostic() {
      this.openDiagnostic = false;
      this.diagnosticProps = {
        idLead: null,
        idProject: null,
        idDiagnostic: null,
        currentStep: 'diagnostic'
      };
      
      // Recarregar lista de diagnósticos se houver lead selecionado
      if (this.savedLeadId || (this.existingLead && this.existingLead.id_doc)) {
        await this.loadExistingDiagnostics();
      }
    },
    
    async loadExistingDiagnostics() {
      const leadId = this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
      const franchiseeId = this.currentUserFranchiseeId;
      
      if (!leadId || !franchiseeId) {
        return;
      }
      
      try {
        // Buscar diagnostics (waterscans) existentes para esse lead e franqueado
        const diagnostics = await DiagnosticServiceFirebase.getDiagnosticsByLeadAndFranchisee(
          leadId,
          franchiseeId
        );
        
        this.existingDiagnostics = diagnostics || [];
        
        // Se a lista de propostas estiver visível, garantir que continue visível
        if (this.showProposalsList) {
          // Manter a lista visível
        }
      } catch (error) {
        console.error('Erro ao recarregar diagnósticos:', error);
      }
    },
    
    async handleDiagnosticSaved(diagnosticData) {
      // Atualizar lista de diagnósticos após salvar
      await this.loadExistingDiagnostics();
      
      // Se a lista não estiver visível, mostrar
      if (!this.showProposalsList && (this.savedLeadId || (this.existingLead && this.existingLead.id_doc))) {
        this.showProposalsList = true;
      }
    },
    
    async createProposalFromWaterscan(diagnostic) {
      if (!diagnostic || !diagnostic.id_doc) {
        this.showSnackbar('Waterscan inválido', 'error');
        return;
      }

      this.loading = true;
      
      try {
        // Carregar dados completos do diagnóstico (com solutionList)
        const diagnosticResponse = await DiagnosticServiceFirebase.getDiagnosticByDiagnosticId(diagnostic.id_doc);
        
        if (!diagnosticResponse) {
          this.showSnackbar('Waterscan não encontrado', 'error');
          return;
        }

        // Carregar solutionList se não estiver presente
        let solutionList = diagnosticResponse.solutionList;
        if (!solutionList || solutionList.length === 0) {
          solutionList = await DiagnosticServiceFirebase.getDiagnosticSolutionByDiagnosticId(diagnostic.id_doc);
        }

        // Montar o diagnóstico completo com solutionList
        const fullDiagnostic = {
          ...diagnosticResponse,
          solutionList: solutionList || [],
          id_doc: diagnostic.id_doc
        };

        // Obter o id_lead do diagnóstico ou usar o savedLeadId
        const leadId = fullDiagnostic.id_lead || diagnostic.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
        
        if (!leadId) {
          this.showSnackbar('Lead não identificado', 'error');
          return;
        }

        // Configurar dados para abrir o ProposalDialog
        this.selectedDiagnostic = fullDiagnostic;
        this.diagnosticProps.idLead = leadId;
        this.selectedProposalId = null; // Nova proposta, não edição
        
        // Abrir o ProposalDialog
        this.openProposal = true;
        
      } catch (error) {
        console.error('Erro ao carregar waterscan:', error);
        this.showSnackbar('Erro ao carregar waterscan', 'error');
      } finally {
        this.loading = false;
      }
    },

    closeProposal() {
      this.openProposal = false;
      this.selectedProposalId = null;
      this.selectedDiagnostic = null;
      this.selectedProposalStatus = null;
      this.diagnosticProps = {
        idLead: null,
        idProject: null,
        idDiagnostic: null,
        currentStep: 'diagnostic'
      };
      // Recarregar lista de propostas se estiver sendo exibida
      if (this.showProposalsList) {
        this.viewExistingProposals();
      }
    },

    cancelEdit() {
      this.editLeadData = false;
      // Restore original data
      if (this.existingLead) {
        this.populateFormFromExistingLead(this.existingLead);
      }
    },

    formatDate(date) {
      if (date === null || date === undefined) return 'Data não disponível';
      
      // Se for um timestamp do Firestore com método toDate
      if (date.toDate && typeof date.toDate === 'function') {
        return date.toDate().toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Se for um objeto timestamp do Firestore com seconds e nanoseconds
      if (date.seconds !== undefined || date._seconds !== undefined) {
        const seconds = date.seconds || date._seconds || 0;
        const dateObj = new Date(seconds * 1000);
        return dateObj.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Se for uma string ISO
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Se for um objeto Date
      if (date instanceof Date) {
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      return 'Data não disponível';
    },

    formatDateFromTimestamp(timestamp) {
      if (!timestamp) return 'Data não disponível';
      
      // Converter timestamp (número) para Date
      // Verificar se é timestamp em milissegundos (13 dígitos) ou segundos (10 dígitos)
      const timestampValue = typeof timestamp === 'number' 
        ? (timestamp.toString().length === 10 ? timestamp * 1000 : timestamp)
        : parseInt(timestamp);
      
      if (isNaN(timestampValue)) return 'Data não disponível';
      
      const date = new Date(timestampValue);
      
      if (isNaN(date.getTime())) return 'Data não disponível';
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getStatusColor(status) {
      const statusColors = {
        'approved': 'success',
        'pending': 'warning',
        'rejected': 'error',
        'draft': 'info'
      };
      return statusColors[status] || 'grey';
    },

    getStatusLabel(status) {
      const statusLabels = {
        'approved': 'Aprovada',
        'pending': 'Pendente',
        'rejected': 'Rejeitada',
        'draft': 'Rascunho'
      };
      return statusLabels[status] || status || 'Sem status';
    },

    openProposal(proposal) {
      // Navegar para a página de detalhes da proposta
      if (proposal.id_diagnostic) {
        this.$router.push({
          name: 'Projects',
          params: { id: proposal.id_diagnostic },
          query: { 
            leadId: this.savedLeadId || (this.existingLead && this.existingLead.id_doc),
            proposalId: proposal.id_doc 
          }
        });
      } else {
        this.showSnackbar('Proposta não possui diagnóstico vinculado', 'warning');
      }
    },

    async generateSimplifiedProposalPdf(proposal) {
      if (!proposal || !proposal.id_doc) {
        this.showSnackbar('Proposta inválida', 'error');
        return;
      }

      // Se a proposta tem waterscan, usar generateDiagnosticProposalPdf
      if (proposal.id_diagnostic) {
        this.generatingPdf = true;
        
        try {
          
          const response = await WaterScanApi.generateDiagnosticProposalPdf(proposal.id_doc);
          
          if (response.status === 200 && response.data?.signedUrl) {
            this.fileLink = response.data.signedUrl;
            this.dialogDownload = true;
          } else {
            showErrorSnackBar("Erro ao gerar proposta. Tente novamente!");
          }
        } catch (error) {
          console.error('Erro ao gerar PDF da proposta:', error);
          showErrorSnackBar('Erro ao gerar PDF da proposta');
        } finally {
          this.generatingPdf = false;
        }
      } else {
        // Para propostas sem waterscan, usar o PDF simplificado
        this.generatingPdf = true;
        
        try {
          const response = await WaterScanApi.generateSimplifiedProposalPdfV2(proposal.id_doc);
          
          // Usar o método downloadPdf do WaterScanApi que já trata a abertura do PDF
          WaterScanApi.downloadPdf(response);
        } catch (error) {
          console.error('Erro ao gerar PDF simplificado:', error);
          showErrorSnackBar('Erro ao gerar PDF simplificado');
        } finally {
          this.generatingPdf = false;
        }
      }
    },

    async pdfRequest(proposal, action) {
      if (!proposal || !proposal.id_doc) {
        this.showSnackbar('Proposta inválida', 'error');
        return;
      }

      if (!proposal.id_diagnostic) {
        this.showSnackbar('Esta proposta não possui waterscan vinculado', 'warning');
        return;
      }

      this.idProposalPDF = proposal.id_doc;
      
      try {
        let response = null;
        
        // Garantir que as soluções do diagnóstico estão atualizadas
        await DiagnosticServiceFirebase.legacyDiagnosticSolution(proposal.id_diagnostic);
        const solution = await DiagnosticServiceFirebase.getDiagnosticSolutionByDiagnosticId(proposal.id_diagnostic);
        
        switch (action) {
          case 'checklist':
            response = await WaterScanApi.generateDiagnosticChecklistPdf(proposal.id_doc);
            if (response.status === 200 && response.data?.signedUrl) {
              this.fileLink = response.data.signedUrl;
              this.dialogDownload = true;
            } else {
              showErrorSnackBar("Erro ao gerar checklist. Tente novamente!");
            }
            break;
          case 'checklist_with_photo':
            const leadId = this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
            response = await WaterScanApi.generateChecklisWithPhotoSevenStepsPdf(proposal.id_doc, leadId);
            if (response.status === 200 && response.data?.signedUrl) {
              this.fileLink = response.data.signedUrl;
              this.dialogDownload = true;
            } else {
              showErrorSnackBar("Erro ao gerar checklist com foto. Tente novamente!");
            }
            break;
        }
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        showErrorSnackBar("Erro ao gerar PDF. Tente novamente!");
      } finally {
        this.idProposalPDF = null;
      }
    },

    async pdfRequestFromDiagnostic(diagnostic, action) {
      if (!diagnostic || !diagnostic.id_doc) {
        this.showSnackbar('Waterscan inválido', 'error');
        return;
      }

      this.idProposalPDF = diagnostic.id_doc;
      
      try {
        let response = null;
        
        // Obter o leadId do diagnóstico ou usar o savedLeadId
        const leadId = diagnostic.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
        console.log('leadId', leadId);
        console.log('diagnostic', diagnostic);
        switch (action) {
          case 'checklist':
            // Usar diretamente o id_doc do diagnóstico
            response = await WaterScanApi.generateDiagnosticChecklistPdf(diagnostic.id_doc);
            if (response.status === 200 && response.data?.signedUrl) {
              this.fileLink = response.data.signedUrl;
              this.dialogDownload = true;
            } else {
              showErrorSnackBar("Erro ao gerar checklist. Tente novamente!");
            }
            break;
          case 'checklist_with_photo':
            // Usar diretamente o id_doc do diagnóstico
            response = await WaterScanApi.generateChecklisWithPhotoSevenStepsPdf(diagnostic.id_doc, leadId);
            if (response.status === 200 && response.data?.signedUrl) {
              this.fileLink = response.data.signedUrl;
              this.dialogDownload = true;
            } else {
              showErrorSnackBar("Erro ao gerar checklist com foto. Tente novamente!");
            }
            break;
        }
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        showErrorSnackBar("Erro ao gerar PDF. Tente novamente!");
      } finally {
        this.idProposalPDF = null;
      }
    },
    
    async openProposalForEdit(proposal) {
      // Se tem diagnóstico, carregar o diagnóstico e abrir o ProposalDialog
      if (proposal.id_diagnostic) {
        this.loading = true;
        try {
          // Carregar dados completos do diagnóstico (com solutionList)
          const diagnosticResponse = await DiagnosticServiceFirebase.getDiagnosticByDiagnosticId(proposal.id_diagnostic);
          
          if (diagnosticResponse) {
            // Carregar solutionList se não estiver presente
            let solutionList = diagnosticResponse.solutionList;
            if (!solutionList || solutionList.length === 0) {
              solutionList = await DiagnosticServiceFirebase.getDiagnosticSolutionByDiagnosticId(proposal.id_diagnostic);
            }

            // Montar o diagnóstico completo com solutionList
            const fullDiagnostic = {
              ...diagnosticResponse,
              solutionList: solutionList || [],
              id_doc: proposal.id_diagnostic
            };

            // Configurar dados para abrir o ProposalDialog
            this.selectedDiagnostic = fullDiagnostic;
            this.diagnosticProps.idLead = proposal.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
            this.selectedProposalId = proposal.id_doc;
            this.selectedProposalStatus = proposal.status || 'pending';
            this.openProposal = true;
          } else {
            // Se não encontrar o diagnóstico, abrir apenas a proposta
            this.selectedDiagnostic = null;
            this.diagnosticProps.idLead = proposal.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
            this.selectedProposalId = proposal.id_doc;
            this.selectedProposalStatus = proposal.status || 'pending';
            this.openProposal = true;
          }
        } catch (error) {
          console.error('Erro ao carregar diagnóstico:', error);
          // Em caso de erro, abrir apenas a proposta sem diagnóstico
          this.selectedDiagnostic = null;
          this.diagnosticProps.idLead = proposal.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
          this.selectedProposalId = proposal.id_doc;
          this.selectedProposalStatus = proposal.status || 'pending';
          this.openProposal = true;
        } finally {
          this.loading = false;
        }
        return;
      }
      
      // Para propostas sem waterscan, abrir o ProposalDialog em modo de edição
      this.selectedDiagnostic = null;
      this.diagnosticProps.idLead = proposal.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
      this.diagnosticProps.idProject = null;
      this.diagnosticProps.idDiagnostic = null;
      this.diagnosticProps.currentStep = 'proposal';
      this.selectedProposalId = proposal.id_doc;
      this.selectedProposalStatus = proposal.status || 'pending';
      this.openProposal = true;
    },
    
    getProposalTypeLabel(proposal) {
      const typeMap = {
        'model_performance': 'Modelo Performance',
        'investment': 'Venda do Projeto',
        'pre_fixed': 'Pré-Fixado',
        'management': 'Gestão',
        'kit_sell': 'Venda de Kit'
      };
      
      const proposalType = proposal?.typeProposal?.type;
      return typeMap[proposalType] || 'Tipo não identificado';
    },

    async viewProposal(proposal) {
      // Carregar proposta completa para visualização
      this.loading = true;
      try {
        const fullProposal = await DiagnosticProposalService.getProposalById(proposal.id_doc);
        if (fullProposal) {
          this.selectedProposalForView = {
            ...fullProposal,
            id_doc: proposal.id_doc
          };
          this.showProposalModal = true;
        } else {
          // Se não encontrar, usar os dados básicos da proposta
          this.selectedProposalForView = proposal;
          this.showProposalModal = true;
        }
      } catch (error) {
        console.error('Erro ao carregar proposta:', error);
        // Em caso de erro, usar os dados básicos da proposta
        this.selectedProposalForView = proposal;
        this.showProposalModal = true;
      } finally {
        this.loading = false;
      }
    },

    closeProposalModal() {
      this.showProposalModal = false;
      this.selectedProposalForView = null;
    },

    getStatusColor(status) {
      const colorMap = {
        'pending': 'orange',
        'accepted': 'green',
        'billing_requested': 'orange',
        'billed': 'blue',
      };
      return colorMap[status] || 'grey';
    },

    getStatusLabel(status) {
      const statusMap = {
        'pending': 'Pendente',
        'accepted': 'Aceita',
        'billing_requested': 'Aguardando Faturamento',
        'billed': 'Faturada',
      };
      return statusMap[status] || status || 'Pendente';
    },

    getSolutionName(solutionId) {
      // Buscar nome da solução pelo ID
      if (!solutionId) return 'N/A';
      const solution = this.solutionsData.find(s => s.id === solutionId || s.id === String(solutionId));
      return solution ? (solution.solution || solution.name || 'N/A') : `ID: ${solutionId}`;
    },

    getProposalItems(proposal) {
      // Retornar lista de itens (soluções, produtos e serviços) para exibição
      const items = [];
      
      // Soluções
      const solutions = Array.isArray(proposal.listSolutions) 
        ? proposal.listSolutions 
        : (Array.isArray(proposal.solutionList) ? proposal.solutionList : []);
      
      solutions.forEach(sol => {
        let solutionName = sol.solution || sol.solutionName;
        // Se não tiver nome, buscar pelo ID
        if (!solutionName && sol.solutionId) {
          solutionName = this.getSolutionName(sol.solutionId);
        } else if (!solutionName && sol.id) {
          solutionName = this.getSolutionName(sol.id);
        }
        
        if (solutionName || sol.solution || sol.solutionName || sol.solutionId || sol.id) {
          items.push({
            name: solutionName || 'Solução',
            quantity: parseFloat(sol.quantity || sol.amount || sol.amountSolution || 0),
            unitPrice: parseFloat(sol.unitPrice || sol.price || 0),
            total: parseFloat(sol.total || 0),
            type: 'Solução'
          });
        }
      });
      
      // Produtos
      if (Array.isArray(proposal.listProducts)) {
        proposal.listProducts.forEach(prod => {
          let productName = prod.productName;
          // Se não tiver productName, verificar se product é um ID ou nome
          if (!productName && prod.product) {
            // Se product parece ser um ID (número ou string que pode ser convertida para número)
            const isId = typeof prod.product === 'number' || 
                        (typeof prod.product === 'string' && /^\d+$/.test(prod.product));
            
            if (isId) {
              // Buscar nome pelo ID
              productName = this.getSolutionName(prod.product);
            } else {
              // Se não for ID, usar o próprio valor como nome
              productName = prod.product;
            }
          }
          
          if (productName || prod.product) {
            items.push({
              name: productName || 'Produto',
              quantity: parseFloat(prod.quantity || prod.amount || 0),
              unitPrice: parseFloat(prod.unitPrice || prod.price || 0),
              total: parseFloat(prod.total || 0),
              type: 'Produto'
            });
          }
        });
      }
      
      // Serviços
      if (Array.isArray(proposal.listServices)) {
        proposal.listServices.forEach(serv => {
          let serviceName = serv.serviceName;
          // Se não tiver serviceName, verificar se service é um ID ou nome
          if (!serviceName && serv.service) {
            // Se service parece ser um ID (número ou string que pode ser convertida para número)
            const isId = typeof serv.service === 'number' || 
                        (typeof serv.service === 'string' && /^\d+$/.test(serv.service));
            
            if (isId) {
              // Buscar nome pelo ID
              serviceName = this.getSolutionName(serv.service);
            } else {
              // Se não for ID, usar o próprio valor como nome
              serviceName = serv.service;
            }
          }
          
          if (serviceName || serv.service) {
            items.push({
              name: serviceName || 'Serviço',
              quantity: parseFloat(serv.quantity || serv.amount || 0),
              unitPrice: parseFloat(serv.unitPrice || serv.price || 0),
              total: parseFloat(serv.total || 0),
              type: 'Serviço'
            });
          }
        });
      }
      
      return items;
    },

    getProposalTotalFormatted(proposal) {
      const total = this.getProposalTotal(proposal);
      return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },

    async loadSolutionsData() {
      try {
        this.solutionsData = await SolutionsServices.GetSolutions();
      } catch (error) {
        console.error('Erro ao carregar soluções:', error);
      }
    },

    async loadExistingProposals() {
      const leadId = this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
      const franchiseeId = this.currentUserFranchiseeId;
      
      if (!leadId || !franchiseeId) {
        return;
      }

      try {
        const proposals = await DiagnosticProposalService.getProposalsByFranchiseeIdAndLeadId(
          franchiseeId,
          leadId
        );

        // Garantir que propostas antigas tenham status padrão
        this.existingProposals = proposals.map(proposal => ({
          ...proposal,
          status: proposal.status || 'pending',
          acceptedAt: proposal.acceptedAt || null,
          billedAt: proposal.billedAt || null,
          billingOrderId: proposal.billingOrderId || null
        }));

        // Ordenar por data de criação (mais nova primeiro)
        this.existingProposals.sort((a, b) => {
          const dateA = a.createdAt?._seconds 
            ? new Date(a.createdAt._seconds * 1000)
            : a.createdAt?.toDate 
            ? a.createdAt.toDate()
            : new Date(a.createdAt || 0);
          const dateB = b.createdAt?._seconds 
            ? new Date(b.createdAt._seconds * 1000)
            : b.createdAt?.toDate 
            ? b.createdAt.toDate()
            : new Date(b.createdAt || 0);
          return dateB - dateA; // Ordem decrescente (mais nova primeiro)
        });
      } catch (error) {
        console.error('Erro ao recarregar propostas:', error);
      }
    },

    async acceptProposal(proposal) {
      if (!proposal.id_doc) {
        showErrorSnackBar('Erro: ID da proposta não encontrado.');
        return;
      }

      try {
        this.loading = true;
        await DiagnosticProposalService.acceptProposal(proposal.id_doc);
        showSuccessSnackBar('Proposta aceita com sucesso!');
        
        // Recarregar lista de propostas
        await this.loadExistingProposals();
      } catch (error) {
        console.error('Erro ao aceitar proposta:', error);
        showErrorSnackBar('Erro ao aceitar proposta. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    async generateBillingOrder(proposal) {
      if (!proposal.id_doc) {
        showErrorSnackBar('Erro: ID da proposta não encontrado.');
        return;
      }

      if (proposal.status !== 'accepted') {
        showErrorSnackBar('A proposta precisa estar aceita para gerar a ordem de faturamento.');
        return;
      }

      if (proposal.status === 'billing_requested' || proposal.status === 'billed') {
        showErrorSnackBar('Esta proposta já possui uma ordem de faturamento.');
        return;
      }

      try {
        this.loading = true;
        
        // Buscar dados do lead para pré-preencher o formulário
        const leadId = proposal.id_lead || this.savedLeadId || (this.existingLead && this.existingLead.id_doc);
        let leadData = null;
        
        if (leadId) {
          try {
            leadData = await LeadsServiceFirebase.getLeadsById(leadId);
          } catch (error) {
            console.error('Erro ao buscar dados do lead:', error);
            // Continuar mesmo sem dados do lead
          }
        }
        
        // Armazenar proposta selecionada e dados do lead
        this.selectedProposalForBilling = proposal;
        this.billingOrderLeadData = leadData || this.existingLead;
        
        // Abrir o modal de coleta de dados
        this.openBillingOrderDialog = true;
      } catch (error) {
        console.error('Erro ao preparar ordem de faturamento:', error);
        showErrorSnackBar('Erro ao preparar ordem de faturamento. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    closeBillingOrderDialog() {
      this.openBillingOrderDialog = false;
      this.selectedProposalForBilling = null;
      this.billingOrderLeadData = null;
    },

    async confirmBillingOrder(billingData) {
      if (!this.selectedProposalForBilling || !this.selectedProposalForBilling.id_doc) {
        showErrorSnackBar('Erro: Proposta não encontrada.');
        return;
      }

      try {
        this.generatingBillingOrder = true;
        this.openBillingOrderDialog = false;
        
        // Criar ordem de faturamento no Firebase com os dados coletados
        // Isso já atualiza a proposta para status 'billing_requested'
        const billingOrderId = await BillingOrderService.createBillingOrder(
          this.selectedProposalForBilling.id_doc,
          this.selectedProposalForBilling,
          billingData
        );
        
        // Verificar se o envio para API está habilitado
        if (BillingOrderApiService.isApiSendEnabled()) {
          // Enviar ordem de faturamento para a API externa para gerar a fatura real
          const [success, apiResponse] = await BillingOrderApiService.sendBillingOrder(
            billingData,
            this.selectedProposalForBilling
          );
          
          if (success) {
            // Quando a API externa confirmar sucesso, atualizar status para 'billed'
            // Recarregar a proposta atualizada primeiro
            await this.loadExistingProposals();
            const updatedProposal = this.existingProposals.find(p => p.id_doc === this.selectedProposalForBilling.id_doc);
            
            if (updatedProposal) {
              await BillingOrderService.updateProposalToBilled(
                updatedProposal.id_doc,
                updatedProposal
              );
            }
            
            showSuccessSnackBar(`Ordem de Faturamento gerada e cobrança lançada com sucesso! ID: ${billingOrderId}`);
          } else {
            // Mesmo se a API externa falhar, a ordem foi criada no Firebase com status 'billing_requested'
            showErrorSnackBar('Ordem criada no sistema (Faturamento Solicitado), mas houve erro ao processar na API externa. Verifique os logs.');
          }
        } else {
          // API desabilitada: apenas criar ordem no Firebase com status 'billing_requested'
          showSuccessSnackBar(`Ordem de Faturamento solicitada com sucesso! ID: ${billingOrderId} (Status: Faturamento Solicitado)`);
        }
        
        // Recarregar lista de propostas
        await this.loadExistingProposals();
        
        // Limpar dados
        this.selectedProposalForBilling = null;
        this.billingOrderLeadData = null;
      } catch (error) {
        console.error('Erro ao gerar ordem de faturamento:', error);
        showErrorSnackBar('Erro ao gerar ordem de faturamento. Tente novamente.');
      } finally {
        this.generatingBillingOrder = false;
      }
    },

    viewBillingOrder(proposal) {
      if (!proposal || !proposal.billingOrderId) {
        showErrorSnackBar('Esta proposta não possui ordem de faturamento vinculada.');
        return;
      }
      console.log('Abrindo visualização da ordem de faturamento:', {
        proposalId: proposal.id_doc,
        billingOrderId: proposal.billingOrderId
      });
      this.selectedBillingOrderId = proposal.billingOrderId;
      this.openBillingOrderViewDialog = true;
    },

    closeBillingOrderViewDialog() {
      this.openBillingOrderViewDialog = false;
      this.selectedBillingOrderId = null;
    },

    getProposalSubtotal(proposal) {
      if (!proposal) return 0;

      const calculateListTotal = (list) => {
        if (!list || !Array.isArray(list)) return 0;
        return list.reduce((sum, item) => {
          const isSelected = item.selected !== false;
          if (isSelected && item.total) {
            return sum + Number(item.total || 0);
          }
          return sum;
        }, 0);
      };

      return calculateListTotal(proposal.listSolutions)
        + calculateListTotal(proposal.listServices)
        + calculateListTotal(proposal.listProducts);
    },

    getProposalDiscountAmount(proposal) {
      if (!proposal?.typeProposal?.hasDiscount) return 0;

      const subtotal = this.getProposalSubtotal(proposal);
      const discountType = proposal.typeProposal.discountType;
      const discountValue = Number(proposal.typeProposal.discountValue || 0);
      let discount = 0;

      if (discountType === 'monetary') {
        discount = discountValue;
      } else if (discountType === 'percentage') {
        discount = subtotal * discountValue / 100;
      }

      return Math.min(Math.max(discount, 0), subtotal);
    },
    
    getProposalTotal(proposal) {
      try {
        // Calcular total a partir de listSolutions, listServices e listProducts
        const calculateListTotal = (list) => {
          if (!list || !Array.isArray(list)) return 0;
          return list.reduce((sum, item) => {
            // Considerar apenas itens selecionados, ou todos se não tiver propriedade selected
            const isSelected = item.selected !== false; // Se não tiver selected, assume true
            if (isSelected && item.total) {
              return sum + Number(item.total || 0);
            }
            return sum;
          }, 0);
        };
        
        const solutionsTotal = calculateListTotal(proposal.listSolutions);
        const servicesTotal = calculateListTotal(proposal.listServices);
        const productsTotal = calculateListTotal(proposal.listProducts);
        
        let total = solutionsTotal + servicesTotal + productsTotal;
        
        // Aplicar desconto se houver
        if (proposal.typeProposal?.hasDiscount) {
          const discountType = proposal.typeProposal.discountType;
          const discountValue = Number(proposal.typeProposal.discountValue || 0);
          
          if (discountType === 'monetary') {
            total = total - discountValue;
          } else if (discountType === 'percentage') {
            total = total - (total * discountValue / 100);
          }
        }
        
        return total >= 0 ? total : 0;
      } catch (error) {
        console.error('Erro ao calcular total da proposta:', error);
        return 0;
      }
    },
    
    formatCurrencyValue(value) {
      return Utils.formatCurrency(value);
    },
    
    async loadLeadsList() {
      if (!this.currentUserFranchiseeId) {
        return;
      }

      this.loadingLeadsList = true;
      try {
        const leads = await LeadsServiceFirebase.getLeadsByFranchisee(this.currentUserFranchiseeId);
        this.leadsList = leads || [];
      } catch (error) {
        console.error('Erro ao carregar leads:', error);
        this.showSnackbar('Erro ao carregar lista de leads', 'error');
        this.leadsList = [];
      } finally {
        this.loadingLeadsList = false;
      }
    },
    
    filterLeadsByName() {
      // O filtro é feito automaticamente pelo computed filteredLeadsList
      // Este método pode ser usado para ações adicionais se necessário
    },
    
    selectLeadFromList(lead) {
      // Preencher os campos de busca com os dados do lead selecionado
      if (lead.document) {
        this.searchDocument = lead.document;
        const digitsOnly = lead.document.replace(/\D/g, '');
        if (digitsOnly.length <= 11) {
          this.searchDocumentMask = "###.###.###-##";
        } else {
          this.searchDocumentMask = "##.###.###/####-##";
        }
      }
      
      if (lead.phone) {
        this.searchPhone = lead.phone;
      } else if (lead.responsible_phone) {
        this.searchPhone = lead.responsible_phone;
      }
      
      // Fechar a listagem
      this.showLeadsList = false;
      
      // Verificar o lead automaticamente
      this.checkLead();
    }
  },

  mounted() {
    // Carregar dados de soluções para visualização de propostas
    this.loadSolutionsData();
  }
};
</script>

<style scoped>
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.proposal-card {
  transition: all 0.3s ease;
  border-radius: 8px !important;
}

.proposal-card:hover {
  transform: translateY(-4px);
  border-color: #1976d2 !important;
}

.waterscan-card {
  transition: all 0.3s ease;
  border-radius: 8px !important;
}

.waterscan-card:hover {
  transform: translateY(-4px);
  border-color: #00acc1 !important;
}

.info-item {
  padding: 8px;
  border-radius: 4px;
  background-color: #f5f5f5;
}

.info-label {
  font-size: 12px;
  color: #757575;
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #212121;
}

.lead-card {
  transition: all 0.3s ease;
  border-radius: 8px !important;
}

.lead-card:hover {
  transform: translateY(-4px);
  border-color: #1976d2 !important;
}

.v-card-text {
  padding: 16px !important;
}

.v-card-actions {
  padding: 8px 16px !important;
}
</style>
